import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Copy, Check, Code, Database, FileJson, Key, Terminal } from 'lucide-react';
import { LandingHeader } from '../../components/layout/landing-header';

const FeedExportApiDocs: React.FC = () => {
  const navigate = useNavigate();
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  const copyToClipboard = (text: string, section: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const CopyButton = ({ text, section }: { text: string; section: string }) => (
    <button
      onClick={() => copyToClipboard(text, section)}
      className="absolute top-2 right-2 p-2 rounded-md bg-gray-700 hover:bg-gray-600 transition-colors"
      title="Copy to clipboard"
    >
      {copiedSection === section ? (
        <Check className="h-4 w-4 text-green-400" />
      ) : (
        <Copy className="h-4 w-4 text-gray-400" />
      )}
    </button>
  );

  const phpBasicExample = `<?php
/**
 * PageMind API - Basic Product Feed Export
 */

$apiKey = 'pk_your_api_key_here';
$siteId = 'your-site-uuid';
$categoryName = 'Bicycle Pumps';

$baseUrl = 'https://api.pagemind.ai/api';

$endpoint = sprintf(
    '%s/sites/%s/categories/%s/export/enriched-feed?format=json&includeEnrichments=true',
    $baseUrl,
    $siteId,
    urlencode($categoryName)
);

$ch = curl_init($endpoint);

curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
        'Authorization: Bearer ' . $apiKey,
        'Content-Type: application/json',
        'Accept: application/json'
    ],
    CURLOPT_TIMEOUT => 120,
]);

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
$error = curl_error($ch);
curl_close($ch);

if ($error) {
    die("cURL Error: " . $error);
}

if ($httpCode !== 200) {
    die("API Error (HTTP $httpCode): " . $response);
}

$products = json_decode($response, true);

foreach ($products as $product) {
    echo "Product: " . $product['title'] . "\\n";
    echo "  SKU: " . $product['sku'] . "\\n";
    echo "  Price: " . $product['price'] . " " . $product['currency'] . "\\n";
    echo "  Q&A Count: " . $product['qa_count'] . "\\n";
    echo "\\n";
}
?>`;

  const phpAdvancedExample = `<?php
/**
 * PageMind API - Full Export with AEO Content
 */

class PageMindAPI {
    private $apiKey;
    private $baseUrl;
    private $siteId;

    public function __construct(string $apiKey, string $siteId, string $baseUrl = 'https://api.pagemind.ai/api') {
        $this->apiKey = $apiKey;
        $this->siteId = $siteId;
        $this->baseUrl = rtrim($baseUrl, '/');
    }

    public function exportEnrichedFeed(string $categoryName, array $options = []): array {
        // aeoLanguage is intentionally omitted from defaults: when unset and
        // includeProductAEO=true, the API falls back to the site's
        // aeo_source_language. Pass an explicit language via $options to
        // override (e.g. 'es' for Spanish output regardless of site source).
        $defaultOptions = [
            'format' => 'json',
            'includeEnrichments' => 'true',
            'includeProductQuestions' => 'true',
            'includeCategoryAEO' => 'true',
            'includeAEOQuestions' => 'true',
            'aeoStatus' => 'approved',
            'includeReviewerInfo' => 'true',
            'includeMetadata' => 'true',
        ];

        $params = array_merge($defaultOptions, $options);
        $queryString = http_build_query($params);

        $endpoint = sprintf(
            '%s/sites/%s/categories/%s/export/enriched-feed?%s',
            $this->baseUrl,
            $this->siteId,
            urlencode($categoryName),
            $queryString
        );

        $ch = curl_init($endpoint);

        curl_setopt_array($ch, [
            CURLOPT_RETURNTRANSFER => true,
            CURLOPT_HTTPHEADER => [
                'Authorization: Bearer ' . $this->apiKey,
                'Content-Type: application/json',
                'Accept: application/json'
            ],
            CURLOPT_TIMEOUT => 300,
        ]);

        $response = curl_exec($ch);
        $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
        $error = curl_error($ch);
        curl_close($ch);

        if ($error) {
            throw new Exception("cURL Error: " . $error);
        }

        if ($httpCode !== 200) {
            throw new Exception("API Error (HTTP $httpCode): " . $response);
        }

        return json_decode($response, true);
    }
}

// Usage
try {
    $api = new PageMindAPI('pk_your_api_key', 'your-site-uuid');
    $products = $api->exportEnrichedFeed('Bicycle Tires', [
        'aeoLanguage' => 'es',
        'aeoStatus' => 'approved',
    ]);

    echo "Exported " . count($products) . " products\\n";

    foreach ($products as $product) {
        echo "- " . $product['title'] . "\\n";

        if (!empty($product['aeo_faq_questions'])) {
            $questions = explode(' | ', $product['aeo_faq_questions']);
            echo "  FAQs: " . count($questions) . " questions\\n";
        }
    }
} catch (Exception $e) {
    echo "Error: " . $e->getMessage() . "\\n";
}
?>`;

  const curlExample = `curl -X GET \\
  "https://api.pagemind.ai/api/sites/{siteId}/categories/Bicycle%20Tires/export/enriched-feed?format=json&includeEnrichments=true&includeCategoryAEO=true&includeAEOQuestions=true&includeMetadata=true" \\
  -H "Authorization: Bearer pk_your_api_key" \\
  -H "Content-Type: application/json"`;

  const jsonResponse = `{
  // Identifiers
  "id": "faeeddc0-8ee7-40a5-9292-e617f9832c1f",
  "sku": "ES-19794756",
  "item_group_id": "ES-624040",

  // Core product fields + original feed specifications (preserved by name)
  "title": "American Classic Cubierta De Carretera Timekeeper",
  "brand": "american-classic",
  "category": "Cycling > Bicycle Parts > Bicycle Tires",
  "price": "49.44",
  "currency": "EUR",
  "description": "...",
  "url": "https://example.com/product/...",
  "gtin": "...", "mpn": "...", "image_link": "...", "availability": "in stock",
  "size": "700x35", "color": "Black",
  // ... plus every other attribute from your feed's additional_data

  // Q&A metrics (with includeEnrichments=true, default)
  "qa_count": 5,
  "qa_answered": 3,
  "category_question_matches": 2,

  // Category AEO (with includeCategoryAEO=true)
  "aeo_featured_snippet": "Category-level content...",
  "aeo_category_purpose": "What this category is for...",
  "aeo_buyer_guide_intro": "How to choose a product in this category...",
  "aeo_narrative": "Longer category narrative...",
  "aeo_category_status": "approved",

  // Product AEO summary (with includeProductAEO=true + includeProductSummary=true, default)
  "aeo_product_why_buy": "Top reasons to buy...",
  "aeo_product_full_description": "Extended product description...",
  "aeo_product_best_for": "Gravel cyclists seeking puncture protection",
  "aeo_product_differentiators": "Reflective sidewall | Tubeless-ready | ...",
  "aeo_product_buyer_profiles": "Weekend warrior (85%): ... | Commuter (72%): ...",
  "aeo_product_use_cases": "Long-distance gravel (92%): ... | Mixed-terrain commuting (81%): ...",
  "aeo_product_benefits": "Puncture protection: ... [evidence] | ...",

  // Product FAQs (with includeProductFaqs=true + faqFormat=combined)
  "aeo_product_faqs": "Q1:A1 | Q2:A2 | Q3:A3",
  "aeo_product_faq_count": 3,

  // Product comparison (with includeComparisonData=true)
  "aeo_comparison_intro": "How this product compares...",
  "aeo_comparison_excels": "Puncture protection | Tubeless setup",
  "aeo_comparison_consider_alternatives": "Lower weight | Smaller profile",
  "aeo_comparison_verdict": "Best for gravel cyclists...",
  "aeo_comparison_ideal_buyer": "Riders who...",
  "aeo_comparison_brand_tiers": "{...JSON: good/better/best tiers with source_reference...}",
  "aeo_comparison_competitor_alternatives": "{...JSON: competitor products with source_reference...}",

  // Reviewer metadata (with includeReviewerInfo=true)
  "aeo_product_status": "published",
  "aeo_product_updated_at": "2026-04-20T11:58:57.606Z",
  "aeo_product_approved_at": "2026-03-12T08:12:18.054Z",
  "aeo_product_reviewer": "Clara Coll",
  "aeo_product_last_published_at": "2026-03-19T10:21:11.000Z",
  "aeo_category_approved_at": "...",
  "aeo_category_reviewer": "PageMind AI",
  "aeo_category_generated_at": "..."
}`;

  const jsonResponseVersioning = `{
  "id": "faeeddc0-8ee7-40a5-9292-e617f9832c1f",
  "sku": "ES-19794756",
  "item_group_id": "ES-624040",
  "title": "American Classic Cubierta De Carretera Timekeeper",
  "...": "all normal row fields as above...",

  "aeo_content_hash": "69dbb6d09a853968c7356b13f22351adb92d294ffc90d5286580a77038297fc6",
  "aeo_content_updated_at": "2026-04-20T11:58:57.606Z",

  "section_versions": {
    "aeo_product_why_buy":          { "hash": "7af8ce...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_product_full_description": { "hash": "2e4d0d...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_product_differentiators":  { "hash": "4b73f5...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_product_best_for":         { "hash": "b09cc3...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_product_buyer_profiles":   { "hash": "46be00...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_product_use_cases":        { "hash": "ea5339...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_product_benefits":         { "hash": "1a9d21...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_product_faqs":             { "hash": "ad2173...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_comparison_intro":         { "hash": "c0c1ff...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_comparison_excels":        { "hash": "22944b...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_comparison_consider_alternatives": { "hash": "795a8d...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_comparison_verdict":       { "hash": "dc31f3...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_comparison_ideal_buyer":   { "hash": "e55c49...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_comparison_brand_tiers":   { "hash": "ab0a8c...", "updated_at": "2026-04-20T11:58:57.606Z" },
    "aeo_comparison_competitor_alternatives": { "hash": "a24be8...", "updated_at": "2026-04-20T11:58:57.606Z" }
  }
}`;

  const multiLanguageExample = `{
  "id": "US-140966337",
  "sku": "US-140966337",
  "item_group_id": "US-140966337",
  "title": "Giant Dash M200 Cycling Computer",
  "brand": "GIANT",
  "...": "(other product fields, returned once — same as today)",

  "aeo_product_faqs": {
    "en": [
      { "question": "What are the main benefits of the Giant Dash M200 cycling computer?", "answer": "The Giant Dash M200 provides excellent real-time data accuracy...", "category": "pre_purchase" },
      { "question": "Who is the Giant Dash M200 best suited for?", "answer": "This cycling computer is ideal for data-driven cyclists...", "category": "pre_purchase" }
    ],
    "es": [
      { "question": "¿Qué hace que el Giant Dash M200 sea una buena opción como ciclocomputador?", "answer": "El Giant Dash M200 es una herramienta potente y precisa...", "category": "pre_purchase" },
      { "question": "¿Para quién está diseñado el ciclocomputador Dash M200?", "answer": "Este ciclocomputador es ideal para ciclistas orientados a los datos...", "category": "pre_purchase" }
    ],
    "fr": [ "..." ],
    "de": [ "..." ]
  },
  "aeo_product_faq_count": 10,

  "aeo_product_status": "published",
  "aeo_product_approved_at": "2026-02-17T08:40:43.924Z",
  "aeo_product_reviewer": "PageMind AI",
  "aeo_product_last_published_at": "2026-02-17T08:40:43.000Z",

  "aeo_translation_date": {
    "es": "2026-05-05T19:56:30.764Z",
    "fr": "2026-05-05T19:56:26.857Z",
    "de": "2026-05-05T19:56:27.696Z"
  },
  "aeo_source_content_version": { "es": 3, "fr": 3, "de": 3 },
  "aeo_product_faq_date": {
    "es": "2026-05-04T11:49:42.682Z",
    "fr": "2026-05-04T11:46:48.671Z",
    "de": "2026-05-04T11:49:43.431Z"
  },

  "section_versions": {
    "aeo_product_faqs": {
      "en": { "hash": "20f30e47...", "updated_at": "2026-05-05T19:47:36.408Z" },
      "es": { "hash": "8bc961...",   "updated_at": "2026-05-05T19:47:36.408Z" },
      "fr": { "hash": "...",         "updated_at": "..." },
      "de": { "hash": "...",         "updated_at": "..." }
    }
  },
  "aeo_content_hash": {
    "en": "f53bd71...",
    "es": "11b3775...",
    "fr": "...",
    "de": "..."
  },
  "aeo_content_updated_at": {
    "en": "2026-05-05T19:47:36.408Z",
    "es": "2026-05-05T19:47:36.408Z",
    "fr": "2026-05-05T19:47:36.408Z",
    "de": "2026-05-05T19:47:36.408Z"
  }
}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <LandingHeader currentPage="docs" />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Database className="h-10 w-10 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900">
              Enriched Product Feed Export API
            </h1>
          </div>
          <p className="text-xl text-gray-600">
            Export your product catalog enriched with Q&A data, AEO content, and metadata.
          </p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <a href="#authentication" className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <Key className="h-6 w-6 text-blue-600 mb-2" />
            <span className="font-medium">Authentication</span>
          </a>
          <a href="#endpoint" className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <Terminal className="h-6 w-6 text-blue-600 mb-2" />
            <span className="font-medium">Endpoint</span>
          </a>
          <a href="#php-examples" className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <Code className="h-6 w-6 text-blue-600 mb-2" />
            <span className="font-medium">PHP Examples</span>
          </a>
          <a href="#response" className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <FileJson className="h-6 w-6 text-blue-600 mb-2" />
            <span className="font-medium">Response Format</span>
          </a>
        </div>

        {/* Authentication Section */}
        <section id="authentication" className="mb-12">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Key className="h-6 w-6" />
            Authentication
          </h2>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <p className="text-gray-600 mb-4">
              Use your API key with the <code className="bg-gray-100 px-2 py-1 rounded">Authorization: Bearer</code> header:
            </p>
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                <code>Authorization: Bearer pk_your_api_key_here</code>
              </pre>
              <CopyButton text="Authorization: Bearer pk_your_api_key_here" section="auth" />
            </div>
            <p className="text-sm text-gray-500 mt-4">
              You can find your API key in the PageMind Admin Dashboard under <strong>Feed Export &gt; API tab</strong>.
            </p>
          </div>
        </section>

        {/* Endpoint Section */}
        <section id="endpoint" className="mb-12">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Terminal className="h-6 w-6" />
            Endpoint
          </h2>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="mb-4">
              <h3 className="font-semibold mb-2">Base URL</h3>
              <code className="text-blue-600 font-mono">https://api.pagemind.ai</code>
              <p className="text-sm text-gray-600 mt-2">
                All API requests are served from <code>api.pagemind.ai</code>. Prepend the base URL to every endpoint path shown in this guide.
              </p>
            </div>
            <div className="mb-4">
              <h3 className="font-semibold mb-2">Endpoint</h3>
              <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium mr-2">
                GET
              </span>
              <code className="text-blue-600 font-mono">
                https://api.pagemind.ai/api/sites/{'{siteId}'}/categories/{'{categoryName}'}/export/enriched-feed
              </code>
            </div>

            <h3 className="font-semibold mb-3 mt-6">Query Parameters</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-semibold">Parameter</th>
                    <th className="text-left py-2 px-3 font-semibold">Default</th>
                    <th className="text-left py-2 px-3 font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">format</td>
                    <td className="py-2 px-3"><code>csv</code></td>
                    <td className="py-2 px-3">Output format: <code>json</code> or <code>csv</code></td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">includeEnrichments</td>
                    <td className="py-2 px-3"><code>true</code></td>
                    <td className="py-2 px-3">Include Q&A metrics and enrichment data</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">includeCategoryAEO</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Include category-level AEO content (featured snippets, buyer guides)</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">includeAEOQuestions</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Include AEO FAQ questions and answers</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">aeoLanguage</td>
                    <td className="py-2 px-3">site's <code>aeo_source_language</code> (typically <code>en</code>) when <code>includeProductAEO=true</code>; <code>en</code> otherwise</td>
                    <td className="py-2 px-3">Language for AEO content (<code>es</code>, <code>en</code>, …). When omitted and <code>includeProductAEO=true</code>, defaults to the site's configured source language (set on the site record; <code>en</code> if unset). For category-only exports (<code>includeProductAEO=false</code>), the default falls back to <code>en</code> regardless of the site's source language — pass <code>aeoLanguage</code> explicitly for those cases. Use <code>all</code> to receive every available translation in one response — see "Multi-language export" below. JSON only.</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">aeoStatus</td>
                    <td className="py-2 px-3"><code>approved</code></td>
                    <td className="py-2 px-3">Content status filter: <code>approved</code> or <code>any</code></td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">includeReviewerInfo</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Include reviewer names and approval timestamps</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">includeMetadata</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Include product creation timestamps</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">filterByAeoStatus</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Only return products that have AEO content matching <code>aeoStatus</code></td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">pendingDelivery</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Only return products with AEO not yet acknowledged as delivered</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">pendingRepublish</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Only return products needing (re)publication: never published OR updated since last publication</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">requireCompleteTranslations</td>
                    <td className="py-2 px-3"><code>true</code></td>
                    <td className="py-2 px-3">
                      Atomic version consistency. When <code>true</code> (the default), a product is only returned if <strong>every</strong> language in the site's <code>aeo_target_languages</code> has a <code>completed</code> translation pinned to the product's <strong>current</strong> AEO generation. Translations whose <code>source_content_version</code> doesn't match the live <code>aeo_page_generations.metadata.version</code> are treated as stale and fail the gate, even if their status is still <code>completed</code> — this guarantees you never receive a freshly-regenerated English with the prior generation's translations attached. <code>pending</code>, <code>processing</code>, <code>failed</code>, and <code>stale</code> also fail the gate. Pass <code>requireCompleteTranslations=false</code> to opt out and accept partial-translation responses (e.g. internal triage). No-op when the site has no <code>aeo_target_languages</code> configured.
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">includeProductAEO</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Include product-level AEO content (why_buy, best_for, FAQs, etc.)</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">includeComparisonData</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Include product comparison tables (brand tiers, competitor alternatives, attribute comparison). Requires <code>includeProductAEO=true</code>.</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">includeProductSummary</td>
                    <td className="py-2 px-3"><code>true</code></td>
                    <td className="py-2 px-3">Include product summary fields: <code>aeo_product_why_buy</code>, <code>aeo_product_full_description</code>, <code>aeo_product_best_for</code>, <code>aeo_product_differentiators</code>, <code>aeo_product_use_cases</code>, <code>aeo_product_benefits</code>, <code>aeo_product_buyer_profiles</code>. Requires <code>includeProductAEO=true</code>.</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">includeProductFaqs</td>
                    <td className="py-2 px-3"><code>true</code></td>
                    <td className="py-2 px-3">Include product-level FAQs: <code>aeo_product_faqs</code> (combined) or <code>aeo_product_faq_questions</code> + <code>aeo_product_faq_answers</code> (separate), plus <code>aeo_product_faq_count</code>. Requires <code>includeProductAEO=true</code>.</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">includeProductSpecifications</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Include AEO-translated product specifications (<code>aeo_product_specifications</code>) — translated <code>display_name: value</code> pairs. Distinct from <code>technical_specifications</code> (raw upstream feed, not translated). Requires <code>includeProductAEO=true</code>.</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">includeProductLimitations</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Include limitations content (<code>aeo_product_limitations_assessment</code>, <code>aeo_product_limitations</code>) — known caveats and mitigations for the product. Requires <code>includeProductAEO=true</code>.</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">includeProductRecommendations</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Include alternative product recommendations (<code>aeo_product_recommendations</code>) — pointers to related products with scenarios and reasons. Requires <code>includeProductAEO=true</code>.</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">markAsFetched</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">When <code>true</code>, stamps <code>client_fetched_at = NOW()</code> on the returned products and translations. Use for "since-last-fetch" tracking.</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">includeNormalizedValues</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Include AI-normalized attribute values (Story 67). Output shape controlled by <code>normalizedExportFormat</code>.</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">sku</td>
                    <td className="py-2 px-3"><code>null</code></td>
                    <td className="py-2 px-3">Filter by specific product SKU</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">itemGroupId</td>
                    <td className="py-2 px-3"><code>null</code></td>
                    <td className="py-2 px-3">Filter by variant group. Returns every SKU sharing this <code>item_group_id</code> (from the feed's <code>additional_data</code>). If your feed calls this field <code>id_model</code>, pass that value here.</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">faqFormat</td>
                    <td className="py-2 px-3"><code>separate</code></td>
                    <td className="py-2 px-3">FAQ format: <code>separate</code> (Q|Q|Q and A|A|A) or <code>combined</code> (Q:A|Q:A|Q:A)</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">include_section_versions</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Append per-column <code>{'{ hash, updated_at }'}</code> entries under <code>section_versions</code> (keyed by feed column name) plus a per-row <code>aeo_content_hash</code> rollup and <code>aeo_content_updated_at</code> for incremental sync</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">hashes_only</td>
                    <td className="py-2 px-3"><code>false</code></td>
                    <td className="py-2 px-3">Return the normal row schema with AEO content columns stripped but hashes present. Always JSON. Use for cheap polling — compare <code>aeo_content_hash</code> against your stored value to detect changes.</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">changed_since</td>
                    <td className="py-2 px-3"><code>null</code></td>
                    <td className="py-2 px-3">
                      ISO 8601 timestamp (any precision; <code>2026-05-14T11:43:47.943Z</code> works).
                      Returns products whose AEO content has been touched since the given date — across <strong>any</strong> section
                      (summary, FAQs, comparison, etc.) <strong>or any translation</strong> (every language we serve for this product).
                      If any one of those timestamps is newer than the date you pass, the product is returned with its full payload as configured by the other params.
                      Pair with the <code>X-PageMind-Next-Since</code> response header for safe delta sync — see "Delta-sync watermark" below.
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">changed_section</td>
                    <td className="py-2 px-3"><code>null</code></td>
                    <td className="py-2 px-3">
                      Narrow <code>changed_since</code> to a single section: <code>summary</code>, <code>best_for</code>, <code>buyer_profiles</code>, <code>use_cases</code>, <code>benefits</code>, <code>faq</code>, or <code>comparison</code>.
                      When set, the filter only considers that section's update time (ignores other sections and translations). Leave empty for the default behaviour above.
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 font-mono text-blue-600">cursor</td>
                    <td className="py-2 px-3"><code>null</code></td>
                    <td className="py-2 px-3">Opaque pagination cursor. Absent → start from the beginning. Read the next-page cursor from the <code>Link: rel="next"</code> response header (see Pagination section below). Page size is server-fixed; not configurable.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Input Parameter → Output Fields Mapping */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Input Parameters → Output Fields</h2>
          <p className="text-gray-600 mb-4">
            Each toggle controls a specific group of fields in the response. Defaults are listed in the previous table.
            All product-level groups require <code>includeProductAEO=true</code>; setting it to <code>false</code> suppresses every <code>aeo_product_*</code> and <code>aeo_comparison_*</code> field regardless of the subgroup toggles.
          </p>
          <div className="bg-white p-6 rounded-lg shadow-sm overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-semibold">Input parameter</th>
                  <th className="text-left py-2 px-3 font-semibold">Output fields included when <code>true</code></th>
                  <th className="text-left py-2 px-3 font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">includeProductAEO</td>
                  <td className="py-2 px-3 align-top">(master switch — required for every row below)</td>
                  <td className="py-2 px-3 align-top">When <code>false</code>, all subgroup toggles are ignored.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">includeProductSummary</td>
                  <td className="py-2 px-3 align-top">
                    <code>aeo_product_why_buy</code>, <code>aeo_product_full_description</code>, <code>aeo_product_best_for</code>, <code>aeo_product_differentiators</code>, <code>aeo_product_use_cases</code>, <code>aeo_product_benefits</code>, <code>aeo_product_buyer_profiles</code>
                  </td>
                  <td className="py-2 px-3 align-top">Default <code>true</code> for backwards compatibility.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">includeProductFaqs</td>
                  <td className="py-2 px-3 align-top">
                    <code>aeo_product_faqs</code> (combined) <em>or</em> <code>aeo_product_faq_questions</code> + <code>aeo_product_faq_answers</code> (separate), plus <code>aeo_product_faq_count</code>
                  </td>
                  <td className="py-2 px-3 align-top">Default <code>true</code>. Shape controlled by <code>faqFormat</code>.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">includeComparisonData</td>
                  <td className="py-2 px-3 align-top">
                    <code>aeo_comparison_intro</code>, <code>aeo_comparison_excels</code>, <code>aeo_comparison_consider_alternatives</code>, <code>aeo_comparison_verdict</code>, <code>aeo_comparison_ideal_buyer</code>, <code>aeo_comparison_brand_tiers</code>, <code>aeo_comparison_competitor_alternatives</code>
                  </td>
                  <td className="py-2 px-3 align-top">Brand tiers + competitor alternatives are JSON strings.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">includeProductSpecifications</td>
                  <td className="py-2 px-3 align-top"><code>aeo_product_specifications</code></td>
                  <td className="py-2 px-3 align-top">AEO-translated <code>display_name: value</code> pairs. Distinct from <code>technical_specifications</code> (raw upstream feed, never translated).</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">includeProductLimitations</td>
                  <td className="py-2 px-3 align-top"><code>aeo_product_limitations_assessment</code>, <code>aeo_product_limitations</code></td>
                  <td className="py-2 px-3 align-top">Assessment is the rollup paragraph; the items field is each known limitation joined with <code>{' | '}</code>.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">includeProductRecommendations</td>
                  <td className="py-2 px-3 align-top"><code>aeo_product_recommendations</code></td>
                  <td className="py-2 px-3 align-top">Pointers to alternative products: <code>scenario: name (price) — reason</code>, joined with <code>{' | '}</code>.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">includeCategoryAEO</td>
                  <td className="py-2 px-3 align-top">
                    <code>aeo_featured_snippet</code>, <code>aeo_category_purpose</code>, <code>aeo_buyer_guide_intro</code>, <code>aeo_narrative</code>, <code>aeo_category_status</code>
                  </td>
                  <td className="py-2 px-3 align-top">Sourced from <em>category_aeo_content</em> (per-language native rows) with translation fallback.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">includeAEOQuestions</td>
                  <td className="py-2 px-3 align-top">
                    <code>aeo_faq_questions</code> + <code>aeo_faq_answers</code> (separate), <em>or</em> <code>aeo_faqs</code> (combined), plus <code>aeo_faq_count</code>
                  </td>
                  <td className="py-2 px-3 align-top">Category-level question wheel. Independent of <code>includeProductFaqs</code>.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">includeReviewerInfo</td>
                  <td className="py-2 px-3 align-top">
                    <code>aeo_product_status</code>, <code>aeo_product_updated_at</code>, <code>aeo_product_approved_at</code>, <code>aeo_product_reviewer</code>, <code>aeo_product_last_published_at</code>, <code>aeo_category_approved_at</code>, <code>aeo_category_reviewer</code>, <code>aeo_category_generated_at</code>, <code>aeo_faq_approved_at</code>, <code>aeo_faq_reviewer</code>
                  </td>
                  <td className="py-2 px-3 align-top">Editorial metadata; flat strings, never per-language.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">includeMetadata</td>
                  <td className="py-2 px-3 align-top"><code>product_created_at</code></td>
                  <td className="py-2 px-3 align-top">Product registration timestamp.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">includeEnrichments</td>
                  <td className="py-2 px-3 align-top"><code>qa_count</code>, <code>qa_answered</code>, <code>top_questions</code>, <code>category_question_matches</code></td>
                  <td className="py-2 px-3 align-top">Default <code>true</code>. Q&amp;A coverage signals.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">include_section_versions</td>
                  <td className="py-2 px-3 align-top"><code>section_versions</code>, <code>aeo_content_hash</code>, <code>aeo_content_updated_at</code></td>
                  <td className="py-2 px-3 align-top">Per-column <code>{'{ hash, updated_at }'}</code> for incremental sync. Under <code>aeoLanguage=all</code> these become per-language maps.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600 align-top">includeNormalizedValues / includeAIEnrichedValues / includeHumanCorrections</td>
                  <td className="py-2 px-3 align-top">
                    <code>norm_*</code> / <code>ai_*</code> / <code>corrected_*</code> attribute fields (or <code>resolved_*</code> in <code>resolved</code> format)
                  </td>
                  <td className="py-2 px-3 align-top">Story 67 normalization. Output shape: <code>normalizedExportFormat</code>.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-gray-600 mt-4 text-sm">
            <strong>Always present (no toggle needed):</strong> <code>id</code>, <code>sku</code>, <code>title</code>, <code>brand</code>, <code>category</code>, <code>price</code>, <code>currency</code>, <code>description</code>, <code>url</code>, plus all original feed attributes (<code>technical_specifications</code>, <code>image_link</code>, <code>gtin</code>, …) spread directly from the upstream catalog.
          </p>
        </section>

        {/* Pagination */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Pagination</h2>
          <p className="text-gray-600 mb-4">
            All responses are cursor-paginated with a server-fixed page size of <strong>50 products</strong>.
            The page size is not configurable. Cursor is opaque — treat it as a base64 string and pass it back unchanged.
          </p>

          <h3 className="font-semibold text-lg mb-2 mt-6">Wire contract</h3>
          <ul className="text-gray-700 mb-4 list-disc pl-6 space-y-1 text-sm">
            <li>First request: omit <code>cursor</code> (or send <code>cursor=null</code>) — server starts from the beginning.</li>
            <li>If more pages exist, the response includes a <code>Link</code> header:
              <pre className="bg-gray-100 px-2 py-1 mt-1 inline-block text-xs">{`Link: <https://api.pagemind.ai/api/sites/{siteId}/categories/all/export/enriched-feed?...&cursor=NEXT>; rel="next"`}</pre>
            </li>
            <li>Body shape is unchanged: a bare JSON array (or CSV rows). Pagination metadata lives only in the <code>Link</code> header.</li>
            <li>When the <code>Link: rel="next"</code> header is absent, you have reached the last page.</li>
            <li>All filter params (<code>aeoLanguage</code>, <code>aeoStatus</code>, <code>pendingRepublish</code>, <code>changedSince</code>, <code>category</code>, <code>includeProductSpecifications</code>, etc.) compose with cursor — the <code>Link</code> URL preserves them so you just GET it verbatim to advance.</li>
            <li>Order is stable: <code>pfi.id ASC</code>. The same query+cursor always returns the same products in the same order.</li>
          </ul>

          <h3 className="font-semibold text-lg mb-2 mt-6">Delta-sync watermark</h3>
          <p className="text-gray-700 mb-3 text-sm">
            Every response carries an <code>X-PageMind-Next-Since</code> header. Store this value on your side and pass it as <code>changed_since</code> on your next polling call.
            It's an ISO 8601 timestamp pinned to the moment our server started reading the first page of your current session — same value across every page of one cursor walk.
            Using it instead of your own clock eliminates two failure modes:
          </p>
          <ul className="text-gray-700 mb-4 list-disc pl-6 space-y-1 text-sm">
            <li>
              <strong>Clock skew between your machine and ours.</strong> If your clock is even a few seconds ahead, content updated in that window would be missed when you next poll with <code>changed_since=&lt;your-now&gt;</code>.
            </li>
            <li>
              <strong>Content updated while we were assembling your response.</strong> Our watermark is captured before we run your query, so anything updated after that point reliably shows up on the next cycle.
            </li>
          </ul>
          <pre className="bg-gray-100 px-2 py-1 inline-block text-xs">{`X-PageMind-Next-Since: 2026-05-14T11:43:47.943Z`}</pre>
          <p className="text-gray-700 mt-3 mb-4 text-sm">
            The header is emitted on every response, even when you didn't pass <code>changed_since</code> — so you can opportunistically capture it from your very first poll and start using delta sync from your second poll onwards.
            All pages of one cursor walk return the same value, so it doesn't matter which page you read it from.
          </p>

          <h3 className="font-semibold text-lg mb-2 mt-6">Worked example (PHP)</h3>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
{`<?php
$baseUrl = 'https://api.pagemind.ai/api/sites/{siteId}/categories/all/export/enriched-feed';
$params = [
  'format' => 'json',
  'aeoLanguage' => 'all',
  'includeProductAEO' => 'true',
  'includeComparisonData' => 'true',
  'includeProductSpecifications' => 'true',
  'aeoStatus' => 'approved',
  'filterByAeoStatus' => 'true',
];
$url = $baseUrl . '?' . http_build_query($params);

$allProducts = [];
do {
  $ch = curl_init($url);
  curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
  curl_setopt($ch, CURLOPT_HEADER, true);
  curl_setopt($ch, CURLOPT_HTTPHEADER, ['Authorization: Bearer ' . API_KEY]);
  $response = curl_exec($ch);
  $headerSize = curl_getinfo($ch, CURLINFO_HEADER_SIZE);
  $headers = substr($response, 0, $headerSize);
  $body = substr($response, $headerSize);
  curl_close($ch);

  $page = json_decode($body, true);
  $allProducts = array_merge($allProducts, $page);

  // Parse Link header for next page
  $url = null;
  if (preg_match('/Link:\\s*<([^>]+)>;\\s*rel="next"/i', $headers, $m)) {
    $url = $m[1];  // already includes all query params + cursor
  }
} while ($url !== null);

echo "Fetched " . count($allProducts) . " products in pages of 50\\n";`}
            </pre>
          </div>

          <h3 className="font-semibold text-lg mb-2 mt-6">Worked example (Node.js)</h3>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
{`const allProducts = [];
let url = 'https://api.pagemind.ai/api/sites/{siteId}/categories/all/export/enriched-feed' +
  '?format=json&aeoLanguage=all&includeProductAEO=true&includeComparisonData=true' +
  '&includeProductSpecifications=true&aeoStatus=approved&filterByAeoStatus=true';

while (url) {
  const r = await fetch(url, { headers: { Authorization: 'Bearer ' + API_KEY }});
  if (!r.ok) throw new Error(r.statusText);
  allProducts.push(...(await r.json()));
  // <a, b, c> → tokens. We want <next-url>; rel="next"
  const link = r.headers.get('Link') || '';
  const next = link.match(/<([^>]+)>;\\s*rel="next"/);
  url = next ? next[1] : null;
}
console.log(\`Fetched \${allProducts.length} products in pages of 50\`);`}
            </pre>
          </div>

          <h3 className="font-semibold text-lg mb-2 mt-6">Why server-fixed page size</h3>
          <p className="text-gray-600 text-sm">
            With <code>aeoLanguage=all</code> + every flag-enabled section, a single product
            row can carry ~500 KB of translated content (17 languages × all fields). Server-side
            we cap each page so the response stays comfortably under any practical CDN/proxy
            buffering limit and parses fast on the client. Larger catalogs simply mean more pages,
            never larger pages.
          </p>
        </section>

        {/* cURL Example */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Quick Test with cURL</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{curlExample}</code>
              </pre>
              <CopyButton text={curlExample} section="curl" />
            </div>
          </div>
        </section>

        {/* PHP Examples Section */}
        <section id="php-examples" className="mb-12">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Code className="h-6 w-6" />
            PHP Code Examples
          </h2>

          {/* Basic Example */}
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h3 className="font-semibold text-lg mb-4">Basic Example: Fetch Products with Q&A</h3>
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{phpBasicExample}</code>
              </pre>
              <CopyButton text={phpBasicExample} section="php-basic" />
            </div>
          </div>

          {/* Advanced Example */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="font-semibold text-lg mb-4">Advanced Example: Full Export with AEO Content</h3>
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm max-h-96 overflow-y-auto">
                <code>{phpAdvancedExample}</code>
              </pre>
              <CopyButton text={phpAdvancedExample} section="php-advanced" />
            </div>
          </div>
        </section>

        {/* Incremental Export Example */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Incremental Export (Only Pending Changes)</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <p className="text-gray-600 mb-4">
              To fetch only products that need publishing (never published or updated since last publication):
            </p>
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{`curl -X GET \\
  "https://api.pagemind.ai/api/sites/{siteId}/categories/all/export/enriched-feed?format=json&filterByAeoStatus=true&aeoStatus=approved&pendingRepublish=true&includeProductAEO=true&includeReviewerInfo=true&aeoLanguage=en" \\
  -H "Authorization: Bearer pk_your_api_key" \\
  -H "Content-Type: application/json"`}</code>
              </pre>
            </div>
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-semibold text-yellow-800 mb-2">How it works:</h4>
              <ul className="text-sm text-yellow-700 list-disc list-inside space-y-1">
                <li><code>pendingRepublish=true</code> returns only products that need (re)publishing</li>
                <li>Products are excluded if their <code>approved_at</code> is older than <code>last_published_at</code></li>
                <li>Use <code>aeo_product_last_published_at</code> to see when content was last published</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Response Format Section */}
        <section id="response" className="mb-12">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <FileJson className="h-6 w-6" />
            Response Format (JSON)
          </h2>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{jsonResponse}</code>
              </pre>
              <CopyButton text={jsonResponse} section="json-response" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm mt-6">
            <h3 className="font-semibold text-lg mb-2">With versioning (<code>include_section_versions=true</code> or <code>hashes_only=true</code>)</h3>
            <p className="text-gray-600 mb-4 text-sm">
              Each row gets a <code>section_versions</code> object with one <code>{'{ hash, updated_at }'}</code> entry per AEO feed column, plus a per-row <code>aeo_content_hash</code> rollup you can store against your <code>id_model</code> to detect any change. With <code>hashes_only=true</code>, the AEO content columns are stripped but every other field (including <code>item_group_id</code>) is returned as normal.
            </p>
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{jsonResponseVersioning}</code>
              </pre>
              <CopyButton text={jsonResponseVersioning} section="json-response-versioning" />
            </div>
            <ul className="list-disc ml-6 mt-4 text-sm text-gray-700 space-y-1">
              <li><code>aeo_content_hash</code> is deterministic: a byte-identical response produces the same hash regardless of mode. Store it against your <code>id_model</code> and recompare on the next poll.</li>
              <li><code>section_versions</code> keys match the feed column names the consumer receives. If only <code>aeo_product_faqs</code> changed, only that entry's hash flips.</li>
              <li><strong>The rollup is scoped to what was actually returned.</strong> <code>aeo_content_hash</code> is computed only from columns present in the response — columns you excluded via inclusion flags (<code>includeProductAEO</code>, <code>includeComparisonData</code>, <code>includeProductSummary</code>, <code>includeProductFaqs</code>, <code>includeCategoryAEO</code>) never enter the rollup. Keep these flags stable between the polling call and the stored hash, or you'll see phantom diffs. Upstream AEO content that has no feed column (e.g. internal sections like <code>problem_solver</code>) also never affects the hash.</li>
              <li><strong>Use the hash, not the timestamp, to decide whether to refetch.</strong> <code>aeo_content_updated_at</code> tracks when any underlying AEO section was last regenerated, regardless of whether the output changed — if the AI regenerates a section with identical output, this timestamp moves forward while <code>aeo_content_hash</code> stays the same. The hash is the authoritative "did the content I consume actually change" signal; the timestamp is informational.</li>
            </ul>
          </div>
        </section>

        {/* Multi-language export */}
        <section id="multi-language" className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Multi-language Export (<code>aeoLanguage=all</code>)</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <p className="text-sm text-gray-700 mb-4">
              Pass <code>aeoLanguage=all</code> to receive every available translation in one response instead of issuing one call per language. Translatable AEO fields and per-row hashes become per-language maps; product fields and editorial metadata stay flat. JSON only — combining structured per-language values with CSV cells is rejected with HTTP 400.
            </p>
            <h3 className="font-semibold text-lg mb-2 mt-4">What changes vs. single-language</h3>
            <ul className="list-disc ml-6 mt-2 text-sm text-gray-700 space-y-1">
              <li><strong>Per-language</strong> (value becomes a <code>{'{lang_code: value}'}</code> object): <code>aeo_product_faqs</code>, <code>aeo_product_why_buy</code>, <code>aeo_product_full_description</code>, <code>aeo_product_best_for</code>, <code>aeo_product_differentiators</code>, <code>aeo_product_use_cases</code>, <code>aeo_product_benefits</code>, <code>aeo_product_buyer_profiles</code>, <code>aeo_translation_date</code>, <code>aeo_source_content_version</code>, <code>aeo_product_summary_date</code>, <code>aeo_product_faq_date</code>, <code>aeo_product_best_for_date</code>, <code>aeo_product_use_cases_date</code>, <code>aeo_content_hash</code>, <code>aeo_content_updated_at</code>, and every entry inside <code>section_versions</code>.</li>
              <li><strong>Flat (single value)</strong>: all product/feed fields (<code>id</code>, <code>sku</code>, <code>item_group_id</code>, <code>title</code>, <code>brand</code>, …), <code>aeo_product_faq_count</code>, and editorial metadata (<code>aeo_product_status</code>, <code>aeo_product_updated_at</code>, <code>aeo_product_approved_at</code>, <code>aeo_product_reviewer</code>, <code>aeo_product_last_published_at</code>) — translations don't have their own approval flow, they inherit from source.</li>
              <li><strong>FAQ shape changes</strong>: instead of a string in <code>faqFormat</code>, FAQs become an array of <code>{'{question, answer, category}'}</code> objects per language. The <code>faqFormat</code> param is ignored. Position alignment is preserved by the translation pipeline, so <code>aeo_product_faqs.en[0]</code> always corresponds to <code>aeo_product_faqs.es[0]</code>, etc.</li>
              <li><strong>Atomic version consistency by default.</strong> With <code>requireCompleteTranslations=true</code> (the default — see Query Parameters), products only appear in the response if <em>every</em> site target language has a completed translation pinned to the product's current AEO generation. You will never receive a freshly-regenerated English alongside the previous generation's translations. Pass <code>requireCompleteTranslations=false</code> to opt out.</li>
              <li><strong>Opt-out behaviour:</strong> when <code>requireCompleteTranslations=false</code>, the product is still returned but any language without a completed translation for the current generation is omitted from its per-language map — no silent fallback to source. The source language is always present if source content exists.</li>
              <li><code>aeo_translation_language</code> is removed (redundant — the map keys are the language codes).</li>
              <li><strong>Language code format is per-site configurable.</strong> Default is ISO 639-1 (<code>en</code>, <code>es</code>, <code>fr</code>, …). If your integration uses a different taxonomy (e.g. 3-letter codes), contact the admins to configure a per-site mapping — once enabled, every per-language map in the response uses your codes (content fields, translation metadata, <code>section_versions</code> sub-keys, <code>aeo_content_hash</code>, <code>aeo_content_updated_at</code>). Any language not covered by the configured mapping falls back to its ISO 639-1 code in the response (mixed-format, never silent data loss).</li>
            </ul>

            <h3 className="font-semibold text-lg mb-2 mt-6">Example response (1 product, 4 languages)</h3>
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
                <code>{multiLanguageExample}</code>
              </pre>
              <CopyButton text={multiLanguageExample} section="multi-language-example" />
            </div>

            <h3 className="font-semibold text-lg mb-2 mt-6">Per-language hashes for incremental sync</h3>
            <ul className="list-disc ml-6 mt-2 text-sm text-gray-700 space-y-1">
              <li><code>aeo_content_hash</code> becomes <code>{'{lang_code: hash}'}</code>. Each language's rollup is computed only from the columns where that language has a value. Store it per (id_model, language) and re-fetch only the languages whose hash flipped.</li>
              <li><code>section_versions.&lt;column&gt;</code> entries become <code>{'{lang_code: { hash, updated_at }}'}</code>. Same incremental-sync semantics, narrowed to a specific column.</li>
              <li><strong>Hashes from <code>aeoLanguage=all</code> are not interchangeable with single-language hashes for the same product+language.</strong> The serialized value differs (combined-string FAQ vs. structured array), so the hash differs. Treat <code>all</code> mode as a separate sync stream.</li>
              <li><code>changed_since</code> in <code>aeoLanguage=all</code> mode considers updates across all sections AND all translations: a product is returned whenever any section regenerated since the date, or any language was (re-)translated since the date. The returned payload contains every language we have for that product, not only the ones that triggered the match.</li>
            </ul>
          </div>
        </section>

        {/* Field Reference */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Field Reference</h2>

          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h3 className="font-semibold text-lg mb-4">Identifier Fields</h3>
            <p className="text-sm text-gray-600 mb-4">
              Every row exposes three identifiers with distinct semantics — pick the one that fits how you key your own side of the integration.
            </p>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-semibold">Field</th>
                  <th className="text-left py-2 px-3 font-semibold">Type</th>
                  <th className="text-left py-2 px-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">id</td>
                  <td className="py-2 px-3">uuid</td>
                  <td className="py-2 px-3">Internal PageMind row ID. Stable across updates. Not usually consumed by integrations.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">sku</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">The feed's <code>product_id</code> (e.g. <code>ES-19794756</code>). One per variant. Use the <code>sku</code> query param to fetch a single variant.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">item_group_id</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Variant-group identifier (e.g. <code>ES-624040</code>), sourced from the feed's <code>additional_data.item_group_id</code>. Many rows can share the same <code>item_group_id</code> — one per color/size variant of the same model. If your feed calls this <code>id_model</code>, that's the same value. Use the <code>itemGroupId</code> query param to fetch every variant of a group in one call.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h3 className="font-semibold text-lg mb-4">Q&A Fields</h3>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-semibold">Field</th>
                  <th className="text-left py-2 px-3 font-semibold">Type</th>
                  <th className="text-left py-2 px-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">qa_count</td>
                  <td className="py-2 px-3">integer</td>
                  <td className="py-2 px-3">Total number of questions for this product</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">qa_answered</td>
                  <td className="py-2 px-3">integer</td>
                  <td className="py-2 px-3">Number of questions that have been answered</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">category_question_matches</td>
                  <td className="py-2 px-3">integer</td>
                  <td className="py-2 px-3">Number of category questions mapped to this product</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h3 className="font-semibold text-lg mb-4">AEO Category Fields</h3>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-semibold">Field</th>
                  <th className="text-left py-2 px-3 font-semibold">Type</th>
                  <th className="text-left py-2 px-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_featured_snippet</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">AI-generated featured snippet for the category</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_category_purpose</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Category purpose description</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_buyer_guide_intro</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Introduction to the buyer's guide</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_narrative</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Long-form category narrative</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_category_status</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Content status: <code>draft</code>, <code>pending_review</code>, <code>approved</code></td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_category_generated_at</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">ISO 8601 timestamp when the category AEO was generated (with <code>includeReviewerInfo=true</code>)</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_faq_questions</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Category-level AEO FAQ questions separated by <code> | </code>. Present with <code>includeAEOQuestions=true</code> + <code>faqFormat=separate</code>.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_faq_answers</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Category-level AEO FAQ answers separated by <code> | </code>. Present with <code>includeAEOQuestions=true</code> + <code>faqFormat=separate</code>.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_faq_count</td>
                  <td className="py-2 px-3">integer</td>
                  <td className="py-2 px-3">Count of category-level AEO FAQs</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h3 className="font-semibold text-lg mb-4">Product AEO Fields</h3>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-semibold">Field</th>
                  <th className="text-left py-2 px-3 font-semibold">Type</th>
                  <th className="text-left py-2 px-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_status</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Status: <code>draft</code>, <code>approved</code>, <code>published</code></td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_why_buy</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">AI-generated "why buy" featured snippet</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_full_description</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Extended product description assembled from the AEO summary</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_best_for</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">AI-generated "best for" recommendations</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_differentiators</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Key product differentiators vs competition, joined with <code> | </code></td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_buyer_profiles</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Target buyer profiles with fit score and priorities, joined with <code> | </code></td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_use_cases</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Use cases with fit score and supporting features, joined with <code> | </code></td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_benefits</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Ranked benefits with headline, explanation and evidence, joined with <code> | </code></td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_faqs</td>
                  <td className="py-2 px-3">string · or · object</td>
                  <td className="py-2 px-3">In single-language mode (<code>aeoLanguage=&lt;code&gt;</code>): a string in the selected <code>faqFormat</code> (combined Q:A or two separate fields). In <code>aeoLanguage=all</code>: a per-language object whose values are arrays of <code>{'{question, answer, category}'}</code>. Items are aligned by array position across languages, so <code>aeo_product_faqs.en[0]</code> corresponds to <code>aeo_product_faqs.es[0]</code>.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_faq_questions</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Product FAQ questions only, joined with <code> | </code>. Present with <code>faqFormat=separate</code>.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_faq_answers</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Product FAQ answers only, joined with <code> | </code>. Present with <code>faqFormat=separate</code>.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_faq_count</td>
                  <td className="py-2 px-3">integer</td>
                  <td className="py-2 px-3">Number of FAQs returned for this product</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_specifications</td>
                  <td className="py-2 px-3">string · or · object</td>
                  <td className="py-2 px-3">AEO-translated specifications. Single-language: <code>display_name: value [unit] | …</code>. Under <code>aeoLanguage=all</code>: per-language object (display names + values translated by the AEO pipeline; brand names + numeric enums stay raw). Requires <code>includeProductSpecifications=true</code>. Distinct from <code>technical_specifications</code> (raw feed, never translated).</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_limitations_assessment</td>
                  <td className="py-2 px-3">string · or · object</td>
                  <td className="py-2 px-3">Roll-up paragraph summarizing the product's known limitations. Per-language object under <code>aeoLanguage=all</code>. Requires <code>includeProductLimitations=true</code>.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_limitations</td>
                  <td className="py-2 px-3">string · or · object</td>
                  <td className="py-2 px-3">Each known limitation as <code>limitation [Impact: …] (Mitigation: …)</code>, joined with <code>{' | '}</code>. Per-language object under <code>aeoLanguage=all</code>. Requires <code>includeProductLimitations=true</code>.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_recommendations</td>
                  <td className="py-2 px-3">string · or · object</td>
                  <td className="py-2 px-3">Alternative products as <code>scenario: name (price) — reason</code>, joined with <code>{' | '}</code>. Per-language object under <code>aeoLanguage=all</code>. Requires <code>includeProductRecommendations=true</code>.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_updated_at</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">ISO 8601 timestamp of the last row-level AEO update (DB-side)</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_approved_at</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">ISO 8601 timestamp when the AEO content was approved</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_reviewer</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Name of reviewer who approved the content</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_product_last_published_at</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Last publication date from Publisher Manifest (null if never published)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h3 className="font-semibold text-lg mb-4">Comparison Data Fields</h3>
            <p className="text-gray-600 mb-4 text-sm">
              Requires <code>includeComparisonData=true</code> and <code>includeProductAEO=true</code>.
              Comparison tables include <code>source_reference</code> (your product ID/SKU) on each product so you can look them up in your system.
            </p>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-semibold">Field</th>
                  <th className="text-left py-2 px-3 font-semibold">Type</th>
                  <th className="text-left py-2 px-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_comparison_intro</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Introduction text for the competitive comparison</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_comparison_excels</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Areas where this product excels, separated by <code> | </code></td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_comparison_consider_alternatives</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Reasons to consider alternatives, separated by <code> | </code></td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_comparison_verdict</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Overall verdict summarizing the product's competitive position</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_comparison_ideal_buyer</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Description of the ideal buyer for this product</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_comparison_brand_tiers</td>
                  <td className="py-2 px-3">JSON string</td>
                  <td className="py-2 px-3">Same-brand products as flat <code>{"{"}"models": [...]{"}"}</code>. Each model: <code>source_reference</code> (item_group_id), name, brand, price, currency, image_url, tier (good/better/best), similarity, and <code>attributes</code> array with all comparison attributes.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_comparison_competitor_alternatives</td>
                  <td className="py-2 px-3">JSON string</td>
                  <td className="py-2 px-3">Competitor products as flat <code>{"{"}"models": [...]{"}"}</code>. Each model: <code>source_reference</code> (item_group_id), name, brand, price, currency, image_url, similarity, and <code>attributes</code> array with all comparison attributes.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="font-semibold text-lg mb-4">Metadata Fields</h3>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-semibold">Field</th>
                  <th className="text-left py-2 px-3 font-semibold">Type</th>
                  <th className="text-left py-2 px-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">product_created_at</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">ISO 8601 timestamp when product was added to PageMind</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_category_approved_at</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">ISO 8601 timestamp of content approval</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono text-blue-600">aeo_category_reviewer</td>
                  <td className="py-2 px-3">string</td>
                  <td className="py-2 px-3">Name of the reviewer who approved the content</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Support Section */}
        <section className="bg-blue-50 border border-blue-200 rounded-lg p-8">
          <h2 className="text-xl font-semibold text-blue-900 mb-2">Need Help?</h2>
          <p className="text-blue-800 mb-4">
            Our support team is here to assist you with any questions about the API.
          </p>
          <div className="flex gap-4">
            <Button onClick={() => navigate('/contact')}>
              Contact Support
            </Button>
            <Button variant="outline" onClick={() => window.open('mailto:support@pagemind.ai')}>
              Email Us
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default FeedExportApiDocs;
